import React, { useState, useEffect, useMemo } from "react";
import {
  Loader,
  Table,
  Dropdown,
  Icon,
  Button,
  Label,
  Popup,
  Message,
  Modal,
  Input,
  Checkbox,
} from "semantic-ui-react";
import { Wasteform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import {
  hasPermission,
  isAdmin,
  isMaster,
  isEditor,
  isReviewer,
} from "../../../../utils/checkPermission";
import "./ListWasteForms.scss";
import { BasicDrawer, ConfirmModal } from "../../../Shared";
import { PERIODS, normalizeUnit } from "../../../../utils";
import { formatDateView } from "../../../../utils/formatDate";
import { WasteForm } from "../WasteForm";
//import { wasteCodes } from "../../../../utils/codes";
import { useLanguage } from "../../../../contexts";

import {
  determineWasteUnits,
  convertUnitsFinal,
  wasteCodes,
} from "../../../../utils/dataAndFormulas/wasteForms";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Importa el CSS de react-toastify
import { UnitsForm } from "../UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";
import { DisposalMethodForm } from "../DisposalMethodForm";
import { UpdateDisposalMethodModal } from "../UpdateDisposalMethodModal";
import { Form } from "../../../../api";

const _ = require("lodash");

const wasteFormController = new Wasteform();
const formController = new Form();

// Multi-comprobante: clave normalizada para agrupar por número de comprobante
// sin que mayúsculas/espacios generen comprobantes "distintos" por error de tipeo.
const normalizeVoucherKey = (name) => (name || "").trim().toLowerCase();

export function ListWasteForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData } =
    props;
  const [wasteForms, setWasteForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user, accessToken } = useAuth();
  const { role, site } = user;

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== undefined) {
        setLoading(true);
        try {
          const response = await wasteFormController.getWasteFormsBySiteAndYear(
            accessToken,
            siteSelected,
            yearSelected,
          );

          if (response.code === 200) {
            setWasteForms(response.wasteForms);
          } else {
            setWasteForms([]);
          }
        } catch (error) {
          console.error(error);
          setWasteForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading)
    return (
      <Loader
        active
        inline="centered"
        content="Cargando datos de residuos..."
      />
    );
  //if (size(wasteForms) === 0) return "No hay resultados ";

  // Actualiza un formulario ya cargado en memoria sin disparar onReload().
  // onReload() cambia la prop `reload`, lo que reactiva el useEffect de arriba
  // y pone loading=true — eso desmonta <TablePeriods> (reemplazado por el
  // <Loader>) y con él se pierde cualquier estado local, como el modal de
  // comprobantes abierto. Para poder guardar una edición sin cerrar ese
  // modal, actualizamos `wasteForms` directamente en memoria.
  const patchWasteForm = (itemId, field, fieldPayload) => {
    setWasteForms((prev) =>
      prev.map((f) => (f._id === itemId ? { ...f, [field]: fieldPayload } : f))
    );
  };

  return (
    <div className="list-waste-forms">
      <TablePeriods
        data={wasteForms}
        reload={reload}
        onReload={onReload}
        onPatchItem={patchWasteForm}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        language={language}
        user={user}
      />
    </div>
  );
}

function TablePeriods(props) {
  const {
    data,
    reload,
    onReload,
    onPatchItem,
    accessToken,
    year,
    site,
    t,
    configFormData,
    language,
    user,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  // Multi-comprobante: { field, period, item } del mes/campo cuyo modal de
  // comprobantes está abierto, o null si está cerrado.
  const [voucherPopup, setVoucherPopup] = useState(null);
  // Multi-comprobante: toggle "ver por comprobante" por campo (default true)
  const [viewByVoucherFields, setViewByVoucherFields] = useState({});
  const [unitsRecorded, setUnitsRecorded] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [expandedFields, setExpandedFields] = useState({});

  //para mostrar mensaje de alerta
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Estado para controlar el modal
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Estados para edición múltiple
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para el modal de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showMissingUnitsWarning, setShowMissingUnitsWarning] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);

  // Estado para el modal de método de disposición por defecto (separado del de unidades)
  const [showDisposalMethodModal, setShowDisposalMethodModal] = useState(false);
  const [disposalMethodsRecorded, setDisposalMethodsRecorded] = useState({});
  const [showMissingDisposalMethodWarning, setShowMissingDisposalMethodWarning] =
    useState(false);
  const [showDisposalMethodWarningModal, setShowDisposalMethodWarningModal] =
    useState(false);

  // Estado para el modal de modificar unidades en formularios existentes
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isUpdatingUnits, setIsUpdatingUnits] = useState(false);

  // Estado para el modal de completar método de disposición en formularios existentes
  const [showUpdateDisposalMethodModal, setShowUpdateDisposalMethodModal] =
    useState(false);
  const [isUpdatingDisposalMethods, setIsUpdatingDisposalMethods] =
    useState(false);

  // Estado para el modal de advertencia de unidades
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUnitsModal = () =>
    setShowUnitsModal((prevState) => !prevState);
  const onOpenCloseUpdateUnitsModal = () =>
    setShowUpdateUnitsModal((prevState) => !prevState);
  const onOpenCloseDisposalMethodModal = () =>
    setShowDisposalMethodModal((prevState) => !prevState);
  const onOpenCloseUpdateDisposalMethodModal = () =>
    setShowUpdateDisposalMethodModal((prevState) => !prevState);

  useEffect(() => {
    (async () => {
      try {
        //Buscar

        if (site && year) {
          const fields = determineFields();
          setUniqueFields(fields);
        }
      } catch (error) {
        //console.error(error);
        setUniqueFields([]);
      }
    })();
  }, [configFormData, year, site, reload]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const fetchUnits = async () => {
      try {
        let recordedUnits = {};
        let unitsFromConfig = false;

        // 1. Primero intentar obtener las unidades desde configFormData.fields (Form)
        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.unit && field.name) {
              recordedUnits[field.name] = field.unit;
            }
          });
          if (Object.keys(recordedUnits).length > 0) {
            unitsFromConfig = true;
          }
          console.log(
            "✅ Unidades cargadas desde configFormData:",
            recordedUnits,
          );
        }

        // 2. Si no hay unidades en configFormData, obtener de los formularios guardados
        // COMENTADO: Esta lógica genera confusión. Ahora solo se usan las unidades de configForm.fields
        // if (Object.keys(recordedUnits).length === 0) {
        //   const response = await wasteFormController.getLastUnitFieldsByYear(
        //     accessToken,
        //     site,
        //     year,
        //     uniqueFields
        //   );

        //   if (response.code === 200) {
        //     recordedUnits = response.fields;
        //     console.log("✅ Unidades cargadas desde formularios:", recordedUnits);
        //   }
        // }

        setUnitsRecorded(recordedUnits);

        // 3.b. Método de disposición por defecto: se guarda en el mismo Form,
        // junto a la unidad (configFormData.fields[].defaultDisposalMethod)
        let recordedDisposalMethods = {};
        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.defaultDisposalMethod && field.name) {
              recordedDisposalMethods[field.name] = field.defaultDisposalMethod;
            }
          });
        }
        setDisposalMethodsRecorded(recordedDisposalMethods);

        // 3. Construir selectedUnits basándose solo en lo registrado (sin defaults)
        const values = {};
        uniqueFields.forEach((field) => {
          // Solo usar la unidad si está registrada, no asignar valor por defecto
          values[field] = recordedUnits[field] || "";
        });

        console.log("✅ selectedUnits actualizado:", values);
        setSelectedUnits(values);

        // Mostrar advertencia solo si no hay configFormData configurado y hay unidades de formularios
        setShowUnitsWarning(
          !configFormData && Object.keys(recordedUnits).length > 0,
        );

        // 4. Validar campos activos sin unidades en configFormData
        if (configFormData?.fields && configFormData.fields.length > 0) {
          const activeFieldsWithoutUnit = configFormData.fields.filter(
            (field) => field.active && !field.unit,
          );

          if (activeFieldsWithoutUnit.length > 0) {
            setShowMissingUnitsWarning(true);
          } else {
            setShowMissingUnitsWarning(false);
          }

          const activeFieldsWithoutDisposalMethod = configFormData.fields.filter(
            (field) => field.active && !field.defaultDisposalMethod,
          );
          setShowMissingDisposalMethodWarning(
            activeFieldsWithoutDisposalMethod.length > 0,
          );
        } else {
          setShowMissingUnitsWarning(false);
          setShowMissingDisposalMethodWarning(false);
        }
      } catch (error) {
        console.error("❌ Error al cargar unidades:", error);
        setUnitsRecorded({});
        // Si falla, dejar todos los campos vacíos
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = "";
        });
        setSelectedUnits(values);
      }
    };

    fetchUnits();
  }, [uniqueFields, configFormData, reload]); // ✅ Agregado reload para recargar cuando se guardan unidades

  // Alerta antes de salir de la página si hay cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges && editMode) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges, editMode]);

  // Atajos de teclado para guardar (Ctrl+S) y cancelar (Esc)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editMode) {
        if (e.ctrlKey && e.key === "s") {
          e.preventDefault();
          handleSaveChanges();
        } else if (e.key === "Escape") {
          handleCancelEdit();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editMode, editedValues]);

  // Función para abrir el modal con datos del período seleccionado
  const handleOpenModal = (period) => {
    setSelectedPeriod(data.find((item) => item.period === period));
    //setModalOpen(true);
    onOpenCloseModal();
  };

  // Función para cerrar el modal
  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_waste_form")} ${formatDateView(form.date)}`,
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  // Función para verificar si hay al menos una unidad configurada
  const validateUnitsConfigured = () => {
    // Si no hay configFormData o no hay fields, permitir continuar (usará campos por defecto)
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    // Filtrar solo los campos activos
    const activeFields = configFormData.fields.filter(
      (field) => field.active !== false,
    );

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    // Verificar que haya al menos UNA unidad configurada
    const fieldsWithUnit = activeFields.filter(
      (field) => field.unit && field.unit !== "",
    );

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message:
          t("units_not_configured") ||
          "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades.",
      };
    }

    return { valid: true };
  };

  const handleUnitsFormSave = (units, disposalMethodDefaults = {}) => {
    const hasChangedUnits = configFormData?.fields?.some(
      f => f.unit && units[f.name] && units[f.name] !== f.unit
    );
    if (hasChangedUnits) {
      setPendingUnitsToSave({ units, disposalMethodDefaults });
      setShowLegacyUnitWarning(true);
    } else {
      handleSaveUnits(units, disposalMethodDefaults);
    }
  };

  // Función para guardar unidades (y método de disposición por defecto) en el Form
  const handleSaveUnits = async (units, disposalMethodDefaults = {}) => {
    try {
      // console.log("💾 Iniciando handleSaveUnits...");
      // console.log("📋 configFormData:", configFormData);

      const formId = configFormData?._id;
      const currentFields = configFormData?.fields || [];

      console.log(
        `🔍 formId: ${formId}, currentFields.length: ${currentFields.length}`,
      );

      // ✅ SIEMPRE verificar si ya existe un Form en la base de datos ANTES de crear uno nuevo
      if (!formId) {
        console.log(
          "⚠️ No hay formId en configFormData, verificando en la base de datos...",
        );

        // Verificar si ya existe un Form en la BD
        const existingForms = await formController.getFormsBySite(
          accessToken,
          site,
          "waste",
        );

        console.log(`🔍 Forms existentes en BD: ${existingForms.length}`);

        if (existingForms.length > 0) {
          // ⚠️ Ya existe un Form, NO crear uno nuevo, actualizar el existente
          console.error(
            "❌ ERROR: Ya existe un Form en la BD pero configFormData no lo tiene!",
          );
          console.log("🔄 Usando el Form existente:", existingForms[0]._id);

          const existingFormId = existingForms[0]._id;
          const existingFields = existingForms[0].fields || [];

          // Solo se envían los campos que cambian, de forma atómica (ver
          // updateFormFields en el controlador) — no se pisa el documento
          // entero.
          const changes = existingFields
            .filter((field) => field.name in units)
            .map((field) => ({
              name: field.name,
              unit: units[field.name],
              defaultDisposalMethod:
                disposalMethodDefaults[field.name] ||
                field.defaultDisposalMethod ||
                "",
            }));

          const response =
            changes.length > 0
              ? await formController.updateFormFields(accessToken, existingFormId, changes)
              : { code: 200 };

          if (response.code === 200) {
            toast.success(t("units_updated_successfully"));
            setShowUnitsWarning(false); // Ocultar advertencia
            onReload();
          } else {
            toast.error(t("error_updating_units"));
          }
          return;
        }

        // Si NO existe ningún Form, crear uno nuevo
        console.log("✅ No hay Form en la BD, creando uno nuevo...");
        const allWasteFieldsData = [
          { name: "paper_and_cardboard", code: "re1" },
          { name: "plastic", code: "re2" },
          { name: "fabric_scraps", code: "re3" },
          { name: "metal_clean", code: "re4" },
          { name: "wood", code: "re5" },
          { name: "food_waste", code: "re6" },
          { name: "general_trash", code: "re7" },
          { name: "glass", code: "re8" },
          { name: "leather", code: "re9" },
          { name: "foam_eva", code: "re10" },
          { name: "empty_cans_or_containers", code: "re11" },
          { name: "rubber", code: "re12" },
          { name: "other_non_hazardous_waste", code: "re13" },
          { name: "batteries", code: "Re14" },
          { name: "electronics", code: "Re15" },
          { name: "drums_or_containers_with_product_remains", code: "Re16" },
          { name: "hospital_waste", code: "Re17" },
          { name: "fluorescent_tubes", code: "Re18" },
          { name: "cooking_oil", code: "Re19" },
          { name: "maintenance_oil", code: "Re20" },
          { name: "lubricants", code: "Re21" },
          { name: "paint_waste", code: "Re22" },
          { name: "coal_ashes", code: "Re23" },
          { name: "rubber_hazardous", code: "Re24" },
          { name: "chemicals", code: "Re25" },
          { name: "contaminated_materials", code: "Re26" },
          { name: "ink_cartridges", code: "Re27" },
          { name: "medical_waste", code: "Re28" },
          { name: "other_hazardous_waste", code: "Re29" },
        ];

        const fieldsToCreate = allWasteFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          defaultDisposalMethod: disposalMethodDefaults[field.name] || "",
          standardized_coefficient: 1,
        }));

        const newFormData = {
          module: "waste",
          site: site,
          fields: fieldsToCreate,
        };

        console.log("📝 Creando nuevo Form con data:", newFormData);

        const response = await formController.createForm(
          accessToken,
          newFormData,
        );
        if (response.code === 200 || response._id) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false); // Ocultar advertencia
          onReload();
        } else {
          toast.error(t("error_updating_units"));
        }
      } else {
        // Ya existe un Form, actualizar las unidades
        // console.log("📋 currentFields ANTES de actualizar:", currentFields);
        // console.log("📋 units recibidas del modal:", units);

        // Solo se envían los campos que cambian, de forma atómica (ver
        // updateFormFields en el controlador) — no se pisa el documento
        // entero, para no perder un toggle de 'active' guardado desde
        // Fuentes casi al mismo tiempo.
        const changes = currentFields
          .filter((field) => field.name in units)
          .map((field) => ({
            name: field.name,
            unit: units[field.name],
            defaultDisposalMethod:
              disposalMethodDefaults[field.name] ||
              field.defaultDisposalMethod ||
              "",
          }));

        const response =
          changes.length > 0
            ? await formController.updateFormFields(accessToken, formId, changes)
            : { code: 200 };

        if (response.code === 200) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false); // Ocultar advertencia
          console.log(
            "✅ Llamando onReload() para actualizar configFormData...",
          );
          onReload();
        } else {
          console.error("❌ Error en respuesta:", response);
          toast.error(t("error_updating_units"));
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_units"));
    }
  };

  // Función para guardar el método de disposición por defecto en el Form (modal separado del de unidades)
  const handleSaveDisposalMethods = async (disposalMethodDefaults) => {
    try {
      const formId = configFormData?._id;
      const currentFields = configFormData?.fields || [];

      if (!formId) {
        const existingForms = await formController.getFormsBySite(
          accessToken,
          site,
          "waste",
        );

        if (existingForms.length > 0) {
          const existingFormId = existingForms[0]._id;
          const existingFields = existingForms[0].fields || [];

          const changes = existingFields
            .filter((field) => field.name in disposalMethodDefaults)
            .map((field) => ({
              name: field.name,
              defaultDisposalMethod: disposalMethodDefaults[field.name] || "",
            }));

          const response =
            changes.length > 0
              ? await formController.updateFormFields(accessToken, existingFormId, changes)
              : { code: 200 };

          if (response.code === 200) {
            toast.success(t("disposal_method_updated_successfully"));
            setShowMissingDisposalMethodWarning(false);
            onReload();
          } else {
            toast.error(t("error_updating_disposal_method"));
          }
          return;
        }

        // No hay Form en la BD todavía: no tiene sentido crear uno solo con
        // método de disposición y sin unidades -> pedir que configure unidades primero
        toast.error(
          t("units_not_configured") ||
            "Debe configurar primero las unidades antes del método de disposición.",
        );
        return;
      }

      const changes = currentFields
        .filter((field) => field.name in disposalMethodDefaults)
        .map((field) => ({
          name: field.name,
          defaultDisposalMethod: disposalMethodDefaults[field.name] || "",
        }));

      const response =
        changes.length > 0
          ? await formController.updateFormFields(accessToken, formId, changes)
          : { code: 200 };

      if (response.code === 200) {
        toast.success(t("disposal_method_updated_successfully"));
        setShowMissingDisposalMethodWarning(false);
        onReload();
      } else {
        toast.error(t("error_updating_disposal_method"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_disposal_method"));
    }
  };

  const openUpdateEffluentForm = (period) => {
    //setFieldName(name);
    const form = data.find((item) => item.period === period);
    setModalContent(
      <WasteForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        wasteForm={form}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />,
    );
    setShowModal(true);
  };

  const openNewEffluentForm = (period) => {
    // Validar que las unidades estén configuradas antes de abrir el formulario
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setModalContent(
      <WasteForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />,
    );
    setShowModal(true);
  };

  // Cantidad de períodos, por campo, que tienen dato cargado pero SIN método
  // de disposición -- se muestra en el modal "Completar método de disposición"
  // para saber de antemano cuánto hay para completar en cada campo.
  //
  // Se excluyen los períodos duplicados sin resolver (mismo "period" repetido
  // en `data`): mientras no se elija cuál conservar en "Períodos duplicados",
  // no tiene sentido contarlos como "sin método" -- ese estado es transitorio
  // y se puede resolver solo o completar distinto en cada copia.
  const missingDisposalMethodCounts = useMemo(() => {
    const periodOccurrences = {};
    (data || []).forEach((item) => {
      periodOccurrences[item.period] = (periodOccurrences[item.period] || 0) + 1;
    });

    const counts = {};
    (data || []).forEach((item) => {
      if (periodOccurrences[item.period] > 1) return; // duplicado sin resolver, no contar todavía

      Object.keys(wasteCodes).forEach((field) => {
        if (item[field] && item[field].value && !item[field].disposalMethod) {
          counts[field] = (counts[field] || 0) + 1;
        }
      });
    });
    return counts;
  }, [data]);

  // Total de campos-período ya cargados que están sin método de disposición
  // (distinto de "no hay default configurado" -- esto es sobre datos reales
  // ya guardados en la base, no sobre la configuración para períodos futuros)
  const totalMissingDisposalMethodInLoadedData = useMemo(
    () => Object.values(missingDisposalMethodCounts).reduce((acc, n) => acc + n, 0),
    [missingDisposalMethodCounts],
  );

  // Función para aplicar unidades a formularios existentes (modal separado)
  const handleApplyUnitsToForms = async (unitsToApply) => {
    if (!unitsToApply || Object.keys(unitsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingUnits(true);
    try {
      const response = await wasteFormController.updateUnitsForYear(
        accessToken,
        site,
        year,
        unitsToApply,
      );

      if (response.code === 200) {
        toast.success(
          `${t("units_applied_to_forms") || "Unidades aplicadas a"} ${response.updated} ${t("forms") || "formularios"}`,
        );
        onOpenCloseUpdateUnitsModal();
        onReload();
      } else {
        toast.error(
          t("error_applying_units_to_forms") || "Error al aplicar unidades",
        );
      }
    } catch (error) {
      console.error("Error applying units:", error);
      toast.error(
        t("error_applying_units_to_forms") || "Error al aplicar unidades",
      );
    } finally {
      setIsUpdatingUnits(false);
    }
  };

  // Función para completar el método de disposición en formularios existentes
  // que todavía no lo tengan cargado (nunca sobreescribe uno ya seleccionado,
  // eso lo garantiza el propio endpoint del server)
  const handleApplyDisposalMethodToForms = async (methodsToApply) => {
    if (!methodsToApply || Object.keys(methodsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingDisposalMethods(true);
    try {
      const response = await wasteFormController.updateDisposalMethodForYear(
        accessToken,
        site,
        year,
        methodsToApply,
      );

      if (response.code === 200) {
        toast.success(
          `${t("disposal_method_applied_to_forms") || "Método de disposición completado en"} ${response.updated} ${t("forms") || "formularios"}`,
        );
        onOpenCloseUpdateDisposalMethodModal();
        onReload();
      } else {
        toast.error(
          t("error_applying_disposal_method_to_forms") ||
            "Error al completar el método de disposición",
        );
      }
    } catch (error) {
      console.error("Error applying disposal methods:", error);
      toast.error(
        t("error_applying_disposal_method_to_forms") ||
          "Error al completar el método de disposición",
      );
    } finally {
      setIsUpdatingDisposalMethods(false);
    }
  };

  const onDelete = async () => {
    try {
      //TODO: modificar por controlador correspondiente
      const response = await wasteFormController.deleteWasteForm(
        accessToken,
        dataDeleted._id,
      );
      if (response.code === 200) {
        setIsSuccess(true);
        setModalMessage("Formulario eliminado correctamente!");
      } else {
        setIsSuccess(false);
        setModalMessage("Error al eliminar el formulario.");
      }

      setModalOpen(true); // Mostrar modal
      setDataDelete(""); // Limpiar datos eliminados
      onOpenCloseConfirm(); // Cerrar el modal de confirmación
      onReload(); // Recargar la información
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setModalMessage("Error al eliminar el formulario.");
      setModalOpen(true); // Mostrar modal de error
    }
  };

  // Obtén los nombres de los períodos para las columnas
  const periods = PERIODS;

  // Determinar los campos únicos y si tienen código
  const determineFields = () => {
    const fields = new Set();

    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      Object.keys(wasteCodes).forEach((key) => {
        if (
          configFormData.fields &&
          configFormData.fields.some(
            (field) => field.name === key && field.active,
          )
        ) {
          fields.add(key);
        }
      });
    } else {
      Object.keys(wasteCodes).forEach((key) => {
        fields.add(key);
      });
    }

    return Array.from(fields);
  };

  const hasDataPeriod = (period) => {
    const existsPeriod = data.some((entry) => entry.period === period);
    return existsPeriod;
  };

  // Multi-comprobante: cantidad de comprobantes distintos que aportan a un
  // campo en el año (agrupados por número normalizado).
  const getDistinctVoucherCount = (field) => {
    const keys = new Set();
    data.forEach((item) => {
      (item[field]?.records || []).forEach((r) => keys.add(normalizeVoucherKey(r.voucherNumber)));
    });
    return keys.size;
  };

  // Multi-comprobante: desglose {voucherNumber: {period: value}} para las
  // sub-filas. Se agrupa por número normalizado; se muestra el número tal
  // como se escribió la primera vez que se vio ese comprobante.
  const getVoucherBreakdown = (field) => {
    const byVoucher = {};
    data.forEach((item) => {
      (item[field]?.records || []).forEach((r) => {
        const key = normalizeVoucherKey(r.voucherNumber);
        if (!byVoucher[key]) byVoucher[key] = { displayName: (r.voucherNumber || "").trim() || "-", monthly: {} };
        byVoucher[key].monthly[item.period] = (byVoucher[key].monthly[item.period] || 0) + (r.value || 0);
      });
    });
    return Object.values(byVoucher).map(({ displayName, monthly }) => [displayName, monthly]);
  };

  // Función para formatear números sin decimales y con puntos en miles
  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  // La conversión de unidades permite transformar valores entre diferentes unidades de medida (ej. kg a toneladas).
  // Para mantener las unidades fijas en la tabla, no se convierten los valores; se muestran tal como están registrados.
  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) => item[field]?.value || 0);

    const totalFinal = values.reduce((ac, value) => ac + value, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const formatValue = (num, language) => {
    // const type = language && language === "en" ? "en-US" : "de-DE";
    // let formateado = new Intl.NumberFormat(type, {
    //   // minimumFractionDigits: 2,
    //   // maximumFractionDigits: 2,
    // }).format(num);

    // return formateado;

    return num;
  };

  const getUnitName = (field) => {
    const unitValue = normalizeUnit(selectedUnits[field]);

    // Si no hay unidad configurada, mostrar mensaje indicativo
    if (!unitValue || unitValue === "") {
      return "- - -";
    }

    const unitsArray = determineWasteUnits(field);
    const unitObject = unitsArray.find((u) => u.value === unitValue);
    return unitObject?.name || unitValue;
  };

  const handleUnitChange = (rowIndex, newValue) => {
    setSelectedUnits({ ...selectedUnits, [rowIndex]: newValue.value });
  };

  // ========== FUNCIONES PARA EDICIÓN MÚLTIPLE ==========

  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    setEditedValues((prev) => ({
      ...prev,
      [key]: {
        formId: item?._id || null,
        period: period,
        field,
        value: newValue,
        unit: item?.[field]?.unit || selectedUnits[field],
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges || Object.keys(editedValues).length === 0) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setIsSaving(true);

    try {
      // Separar actualizaciones de creaciones
      const updates = {};
      const creates = [];

      Object.entries(editedValues).forEach(([key, change]) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
          });
        } else {
          updates[key] = {
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
          };
        }
      });

      const response = await wasteFormController.batchUpdateWasteForms(
        accessToken,
        {
          updates,
          creates,
          site,
          year,
          creatorUser: user._id,
        },
      );

      if (response.code === 200) {
        toast.success(
          `✅ ${response.updated || 0} formularios actualizados, ${response.created || 0} creados`,
        );
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error(response.msg || "Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas cancelar la edición?",
      );
      if (!confirmed) return;
    }
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleEditMode = () => {
    if (editMode && hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas salir del modo edición?",
      );
      if (!confirmed) return;
    }
    setEditMode(!editMode);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleExpand = (field) => {
    setExpandedFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const getFieldByGroup = (field, type) => {
    const value = wasteCodes[field];
    return value.startsWith(type);
  };

  return (
    <>
      {/* Botones de control de edición múltiple */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
      <div className="batch-actions">
        {!editMode ? (
          <Popup
            content={t("enable_multiple_edit")}
            trigger={
              <Button
                className="batch-btn primary"
                icon
                //labelPosition="left"
                onClick={toggleEditMode}
                disabled={data.length === 0}
              >
                <Icon name="edit" />
                {/* Editar Múltiples Períodos */}
              </Button>
            }
          />
        ) : (
          <>
            <Button
              className="batch-btn success"
              icon
              labelPosition="left"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
            >
              <Icon name="save" />
              Guardar Cambios
              {hasChanges && ` (${Object.keys(editedValues).length})`}
            </Button>
            <Button
              className="batch-btn cancel"
              icon
              labelPosition="left"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <Icon name="cancel" />
              Cancelar
            </Button>
            {hasChanges && (
              <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                <Icon name="warning sign" />
                {Object.keys(editedValues).length} cambio(s) pendiente(s)
              </span>
            )}
          </>
        )}
      </div>
      )}

      {/* Mensaje de cambios pendientes */}
      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para
            cancelar)
          </p>
        </Message>
      )}

      {/* 🆕 Advertencia de unidades tomadas de formularios */}
      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una
            mejor consistencia, se recomienda configurar las unidades
            explícitamente haciendo clic en el ícono de tres puntos (⋯) en la
            columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-waste-forms__table-wrapper">
        <Table className="waste-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="sticky-col-1 pill-left-start">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-left-end">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  {t("units")}
                  {showMissingUnitsWarning && (
                    <Icon
                      name="warning sign"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowWarningModal(true)}
                      title={t("units_not_configured")}
                    />
                  )}
                  {showMissingDisposalMethodWarning && (
                    <Icon
                      name="warning circle"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowDisposalMethodWarningModal(true)}
                      title={t("disposal_method_not_configured")}
                    />
                  )}
                  {totalMissingDisposalMethodInLoadedData > 0 && (
                    <Icon
                      name="exclamation triangle"
                      color="red"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={onOpenCloseUpdateDisposalMethodModal}
                      title={
                        t("loaded_forms_missing_disposal_method_title") ||
                        "Hay formularios ya cargados sin método de disposición"
                      }
                    />
                  )}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      <Dropdown.Item
                        text={
                          t("configure_disposal_method") ||
                          "Configurar método de disposición"
                        }
                        icon="recycle"
                        onClick={onOpenCloseDisposalMethodModal}
                      />
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={
                            t("update_units_in_forms") ||
                            "Modificar unidades en formularios"
                          }
                          icon="edit"
                          onClick={onOpenCloseUpdateUnitsModal}
                        />
                      )}
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={
                            t("update_disposal_method_in_forms") ||
                            "Completar método de disposición"
                          }
                          icon="tasks"
                          onClick={onOpenCloseUpdateDisposalMethodModal}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell
                colSpan="3"
                textAlign="center"
                className="pill-right"
              >
                {t("report_period")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {/* TODO: separar residuos peligrosos y no peligrosos en secciones distintas */}
            {/* Actualmente se muestran todos los campos juntos */}
            {/* getFieldByGroup(field, "re") → residuos no peligrosos */}
            {/* getFieldByGroup(field, "Re") → residuos peligrosos */}
            {uniqueFields.map((field) => {
              const isExpanded = expandedFields[field];
              const { total, average } = calculateTotalAndAverage(field);
              const distinctVouchers = getDistinctVoucherCount(field);
              const showByVoucher = viewByVoucherFields[field] !== false;
              const hasMismatchedUnit = data.some((item) => {
                const itemUnit = normalizeUnit(item?.[field]?.unit);
                const configuredUnit = normalizeUnit(selectedUnits[field]);
                return itemUnit && configuredUnit && itemUnit !== configuredUnit;
              });

              return (
                <React.Fragment key={field}>
                  {/* Fila resumen */}
                  <Table.Row>
                    <Table.Cell className="sticky-col-1 pill-left-start">
                      {wasteCodes[field]}
                    </Table.Cell>
                    <Table.Cell className="sticky-col-2">{t(field)}</Table.Cell>
                    <Table.Cell textAlign="center" className="pill-left-end">
                      {getUnitName(field)}
                      {hasMismatchedUnit && (
                        <Popup
                          content="Hay meses con unidades diferentes a la configurada"
                          trigger={
                            <Icon
                              name="warning sign"
                              color="orange"
                              size="small"
                              style={{ cursor: "pointer", marginLeft: "4px" }}
                            />
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell className="pill-gap" />
                    <Table.Cell colSpan="3" className="pill-right">
                      <div className="summary-flex">
                        <span className="summary-item">
                          {t("total")}: <strong>{total}</strong>
                        </span>
                        <span className="summary-item">
                          {t("average")}: <strong>{average}</strong>
                        </span>
                        <Button
                          icon
                          size="tiny"
                          className="expand-btn"
                          onClick={() => toggleExpand(field)}
                        >
                          <Icon
                            name={isExpanded ? "chevron up" : "chevron down"}
                          />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>

                  {/* Fila cabecera de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-header-row">
                      <Table.Cell
                        colSpan="7"
                        style={{ padding: 0 }}
                        className="months-full-row"
                      >
                        <div className="months-flex-container">
                          {periods.map((period) => (
                            <div
                              key={period}
                              className="month-flex-cell month-flex-header"
                            >
                              {t(period).slice(0, 3)}
                              {!editMode &&
                                (hasDataPeriod(period) ? (
                                  <Dropdown
                                    inline
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        text={t("edit")}
                                        icon={
                                          <Icon
                                            name="edit"
                                            style={{ color: "gray" }}
                                          />
                                        }
                                        onClick={() =>
                                          openUpdateEffluentForm(period)
                                        }
                                      />
                                      <Dropdown.Item
                                        text={t("delete")}
                                        icon={
                                          <Icon
                                            name="trash"
                                            style={{ color: "red" }}
                                          />
                                        }
                                        onClick={() =>
                                          handleDeleteModal(period)
                                        }
                                      />
                                    </Dropdown.Menu>
                                  </Dropdown>
                                ) : (
                                  <Dropdown
                                    link
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      {(isAdmin(user.role) ||
                                        isEditor(user.role) ||
                                        isMaster(user.role)) && (
                                        <Dropdown.Item
                                          text={t("load_data")}
                                          icon="plus"
                                          onClick={() =>
                                            openNewEffluentForm(period)
                                          }
                                        />
                                      )}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                ))}
                            </div>
                          ))}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* Fila datos de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-data-row">
                      <Table.Cell
                        colSpan="7"
                        style={{ padding: 0 }}
                        className="months-full-row"
                      >
                        <div className="months-flex-container">
                          {periods.map((period) => {
                            const item = data.find((d) => d.period === period);
                            const editKey = `${period}_${field}`;
                            const isEdited =
                              editedValues[editKey] !== undefined;
                            const hasUnit =
                              selectedUnits[field] &&
                              selectedUnits[field] !== "";
                            const itemUnit = normalizeUnit(item?.[field]?.unit);
                            const configuredUnit = normalizeUnit(selectedUnits[field]);
                            const isDifferentUnit =
                              itemUnit &&
                              configuredUnit &&
                              itemUnit !== configuredUnit;
                            let cellContent;

                            if (editMode) {
                              const displayVal = isEdited
                                ? editedValues[editKey].value
                                : (item?.[field]?.value ?? "");
                              cellContent = (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={displayVal}
                                  onChange={(e) =>
                                    handleCellChange(
                                      field,
                                      period,
                                      item,
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  disabled={!hasUnit || isReviewer(user.role)}
                                  style={{
                                    width: "100%",
                                    height: "30px",
                                    border: isEdited
                                      ? "2px solid #fbc02d"
                                      : "1px solid #ccc",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    textAlign: "right",
                                    fontSize: "11px",
                                    background: hasUnit ? "white" : "#f5f5f5",
                                    color: !hasUnit ? "#999" : isReviewer(user.role) ? "#000" : "inherit",
                                  }}
                                  placeholder="-"
                                />
                              );
                            } else {
                              const voucherCount = item?.[field]?.records?.length || 0;
                              cellContent = item?.[field] ? (
                                <span>
                                  {formatNumberWithThousands(item[field].value)}
                                  {isDifferentUnit && (
                                    <span
                                      style={{
                                        fontSize: "0.75em",
                                        color: "#f57c00",
                                        marginLeft: "2px",
                                      }}
                                      title={`Unidad: ${itemUnit} | Configurada: ${configuredUnit}`}
                                    >
                                      ({itemUnit})
                                    </span>
                                  )}
                                  {voucherCount > 1 && (
                                    <span
                                      style={{
                                        marginLeft: "0.3em", background: "#1a2a4a", color: "#fff",
                                        borderRadius: "50%", padding: "0 5px", fontSize: "0.7em",
                                      }}
                                    >
                                      {voucherCount}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                "-"
                              );
                            }

                            return (
                              <div
                                key={period}
                                className="month-flex-cell month-flex-data"
                                style={!editMode ? { cursor: "pointer" } : undefined}
                                title={!editMode ? (t("view_add_voucher_records") || "Ver / agregar comprobantes del mes") : undefined}
                                onClick={
                                  !editMode
                                    ? () => setVoucherPopup({ field, period, item: item || null })
                                    : undefined
                                }
                              >
                                {cellContent}
                              </div>
                            );
                          })}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* Multi-comprobante: toggle + sub-filas por comprobante (expandida) */}
                  {isExpanded && distinctVouchers > 1 && (
                    <>
                      <Table.Row className="months-header-row">
                        <Table.Cell className="sticky-col-1" style={{ padding: "4px 8px" }}>
                          <Checkbox
                            toggle
                            label={t("view_by_voucher") || "Ver por comprobante"}
                            checked={showByVoucher}
                            onChange={(_, d) =>
                              setViewByVoucherFields((prev) => ({ ...prev, [field]: d.checked }))
                            }
                          />
                        </Table.Cell>
                        <Table.Cell colSpan="6" />
                      </Table.Row>
                      {showByVoucher &&
                        getVoucherBreakdown(field).map(([voucherNumber, monthly]) => (
                          <Table.Row key={voucherNumber} className="months-data-row">
                            <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                              <div style={{ padding: "2px 12px", fontSize: "0.78em", color: "#648EBC", fontWeight: 600 }}>
                                <Icon name="file alternate outline" /> {voucherNumber}
                              </div>
                              <div className="months-flex-container">
                                {periods.map((period) => (
                                  <div key={period} className="month-flex-cell month-flex-data" style={{ color: "#888" }}>
                                    {monthly[period] != null ? formatNumberWithThousands(monthly[period]) : "-"}
                                  </div>
                                ))}
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <BasicDrawer show={showModal} close={onOpenCloseModal}>
        {modalContent}
      </BasicDrawer>
      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
        danger
      />

      <ConfirmModal
        open={modalOpen}
        icon={<Icon name={isSuccess ? "check circle outline" : "warning sign"} color={isSuccess ? "green" : "red"} />}
        title={isSuccess ? "Éxito" : "Error"}
        content={modalMessage}
        onCancel={() => setModalOpen(false)}
        cancelText="Cerrar"
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <DisposalMethodForm
        show={showDisposalMethodModal}
        onClose={onOpenCloseDisposalMethodModal}
        onSave={handleSaveDisposalMethods}
        currentDisposalMethods={disposalMethodsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={onOpenCloseUpdateUnitsModal}
        onApply={handleApplyUnitsToForms}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingUnits}
      />

      <UpdateDisposalMethodModal
        show={showUpdateDisposalMethodModal}
        onClose={onOpenCloseUpdateDisposalMethodModal}
        onApply={handleApplyDisposalMethodToForms}
        currentDisposalMethods={disposalMethodsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingDisposalMethods}
        missingCounts={missingDisposalMethodCounts}
      />

      <ConfirmModal
        open={showWarningModal}
        icon={<Icon name="warning sign" color="orange" />}
        title="Unidades sin configurar"
        content={
          <>
            <p>Los campos no tienen unidades asignadas en la configuración.</p>
            <p>
              Por favor configure las unidades haciendo clic en el ícono{" "}
              <strong>⋯</strong> en la columna Unidades.
            </p>
          </>
        }
        onCancel={() => setShowWarningModal(false)}
        onConfirm={() => {
          setShowWarningModal(false);
          onOpenCloseUnitsModal();
        }}
        cancelText="Cerrar"
        confirmText="Configurar unidades"
      />

      <ConfirmModal
        open={showDisposalMethodWarningModal}
        icon={<Icon name="warning circle" color="orange" />}
        title="Método de disposición sin configurar"
        content={
          <>
            <p>
              Los campos no tienen un método de disposición por defecto
              configurado. Sin esto, los períodos nuevos no van a traer el
              método precargado y habrá que seleccionarlo a mano cada vez.
            </p>
            <p>
              Por favor configúrelo haciendo clic en el ícono{" "}
              <strong>⋯</strong> en la columna Unidades y eligiendo "
              {t("configure_disposal_method") ||
                "Configurar método de disposición"}
              ".
            </p>
          </>
        }
        onCancel={() => setShowDisposalMethodWarningModal(false)}
        onConfirm={() => {
          setShowDisposalMethodWarningModal(false);
          onOpenCloseDisposalMethodModal();
        }}
        cancelText="Cerrar"
        confirmText="Configurar método"
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(
            pendingUnitsToSave?.units,
            pendingUnitsToSave?.disposalMethodDefaults,
          );
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />

      {voucherPopup && (
        <VoucherRecordsModal
          voucherPopup={voucherPopup}
          onClose={() => setVoucherPopup(null)}
          accessToken={accessToken}
          site={site}
          year={year}
          user={user}
          t={t}
          canEdit={isAdmin(user.role) || isEditor(user.role) || isMaster(user.role)}
          onSaved={() => {
            setVoucherPopup(null);
            onReload();
          }}
          onSavedKeepOpen={(fieldPayload) => {
            setVoucherPopup((prev) =>
              prev ? { ...prev, item: { ...(prev.item || {}), [prev.field]: fieldPayload } } : prev
            );
            // A propósito NO llamamos a onReload() acá: eso pondría loading=true
            // en el componente padre y desmontaría este modal. En cambio,
            // actualizamos el registro en memoria para que la tabla de fondo
            // (totales, badge de comprobantes) quede sincronizada sin cerrar
            // el modal.
            if (voucherPopup?.item?._id) {
              onPatchItem(voucherPopup.item._id, voucherPopup.field, fieldPayload);
            }
          }}
        />
      )}
    </>
  );
}

// Multi-comprobante: modal para ver los comprobantes de un mes/campo y agregar uno nuevo a mano.
function VoucherRecordsModal({ voucherPopup, onClose, accessToken, site, year, user, t, canEdit = true, onSaved, onSavedKeepOpen }) {
  const { field, period, item } = voucherPopup;
  const [voucherNumber, setVoucherNumber] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  // null = agregando un comprobante nuevo; número = editando existingRecords[editingIndex]
  const [editingIndex, setEditingIndex] = useState(null);

  const existingRecords = item?.[field]?.records || [];
  const currentTotal = item?.[field]?.value;
  const currentUnit = item?.[field]?.unit;

  const resetForm = () => {
    setVoucherNumber("");
    setValue("");
    setEditingIndex(null);
  };

  const handleStartEdit = (i) => {
    if (!canEdit) return;
    setVoucherNumber(existingRecords[i].voucherNumber || "");
    setValue(String(existingRecords[i].value));
    setEditingIndex(i);
  };

  const handleDelete = async (i) => {
    if (!canEdit || !item?._id) return;
    setSaving(true);
    try {
      const newRecords = existingRecords.filter((_, idx) => idx !== i);
      const newTotal = newRecords.reduce((s, r) => s + (r.value || 0), 0);
      // Se preservan unit/disposalMethod/code/etc. del campo -- el update solo
      // debe tocar records/value, nunca pisar el resto del subdocumento.
      await wasteFormController.updateWasteForm(accessToken, item._id, { [field]: { ...item[field], records: newRecords, value: newTotal } });
      toast.success(t("voucher_record_deleted") || "Comprobante eliminado");
      if (editingIndex === i) resetForm();
      onSaved();
    } catch (error) {
      console.error(error);
      toast.error(t("error_saving_voucher_record") || "Error al guardar el comprobante");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      toast.warning(t("voucher_value_required") || "Completá el valor del comprobante");
      return;
    }
    setSaving(true);
    try {
      const isEditing = editingIndex != null;

      // Si el campo ya tenía un valor cargado a mano (sin comprobantes
      // todavía -- records vacío) y este es el primer comprobante que se
      // agrega, ese valor previo se preserva como comprobante inicial. Si
      // no, se pisaba con el total del comprobante nuevo solo.
      const baseRecords =
        !isEditing && existingRecords.length === 0 && currentTotal
          ? [{ voucherNumber: t("previous_manual_value") || "Carga manual previa", value: currentTotal, source: "manual" }]
          : existingRecords;

      const newRecords = isEditing
        ? baseRecords.map((r, idx) =>
            idx === editingIndex ? { ...r, voucherNumber: voucherNumber.trim(), value: numValue } : r
          )
        : [
            ...baseRecords,
            { voucherNumber: voucherNumber.trim(), value: numValue, source: "manual", addedBy: user?._id },
          ];
      const newTotal = newRecords.reduce((s, r) => s + (r.value || 0), 0);
      const fieldPayload = { records: newRecords, value: newTotal };

      if (item?._id) {
        // Se preserva unit/disposalMethod/code/etc. del campo -- el update solo
        // debe tocar records/value, nunca pisar el resto del subdocumento.
        await wasteFormController.updateWasteForm(accessToken, item._id, { [field]: { ...item[field], ...fieldPayload } });
      } else {
        const createResponse = await wasteFormController.createWasteForm(accessToken, {
          site,
          year,
          period,
          creator_user: user._id,
          date: new Date(),
          [field]: fieldPayload,
        });
        // Condición de carrera: otro request creó el formulario de este
        // período justo antes que nosotros. En vez de perder nuestro
        // comprobante, lo agregamos al documento que ganó la carrera.
        if (createResponse?.alreadyExisted && createResponse?.waterForm?._id) {
          const winnerField = createResponse.waterForm?.[field] || {};
          const winnerRecords = winnerField.records || [];
          const mergedRecords = [...winnerRecords, ...newRecords.slice(existingRecords.length)];
          const mergedTotal = mergedRecords.reduce((s, r) => s + (r.value || 0), 0);
          await wasteFormController.updateWasteForm(accessToken, createResponse.waterForm._id, {
            [field]: { ...winnerField, records: mergedRecords, value: mergedTotal },
          });
        }
      }
      toast.success(isEditing ? (t("voucher_record_updated") || "Comprobante actualizado") : (t("voucher_record_added") || "Comprobante agregado"));
      resetForm();
      // Al editar un registro existente dejamos el modal abierto para poder
      // seguir revisando/editando otros comprobantes del mismo mes.
      if (isEditing) {
        onSavedKeepOpen(fieldPayload);
      } else {
        onSaved();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_saving_voucher_record") || "Error al guardar el comprobante");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="tiny">
      <Modal.Header>
        {(t("voucher_records_for") || "Comprobantes de")} {t(period)} — {t(field)}
      </Modal.Header>
      <Modal.Content>
        {existingRecords.length > 0 ? (
          <Table basic="very" compact size="small">
            <Table.Body>
              {existingRecords.map((r, i) => (
                <Table.Row key={i} active={editingIndex === i}>
                  {editingIndex === i ? (
                    <>
                      <Table.Cell>
                        <Input
                          size="mini"
                          fluid
                          value={voucherNumber}
                          onChange={(e) => setVoucherNumber(e.target.value)}
                          disabled={saving}
                          autoFocus
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Input
                          size="mini"
                          type="number"
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          disabled={saving}
                          style={{ width: 90 }}
                        />
                      </Table.Cell>
                      {canEdit && (
                        <Table.Cell textAlign="right" collapsing>
                          <Button
                            icon
                            circular
                            color="green"
                            size="mini"
                            className="voucher-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleSave()}
                            loading={saving}
                            disabled={saving}
                            title={t("save_changes") || "Guardar cambios"}
                          >
                            <Icon name="check" style={{ margin: 0 }} />
                          </Button>
                          <Button
                            icon
                            circular
                            size="mini"
                            className="voucher-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, backgroundColor: "#B7B7B7" }}
                            onClick={resetForm}
                            disabled={saving}
                            title={t("cancel_edit") || "Cancelar edición"}
                          >
                            <Icon name="close" style={{ margin: 0, color: "#000" }} />
                          </Button>
                        </Table.Cell>
                      )}
                    </>
                  ) : (
                    <>
                      <Table.Cell>{r.voucherNumber || "-"}</Table.Cell>
                      <Table.Cell textAlign="right">{r.value} {currentUnit}</Table.Cell>
                      {canEdit && (
                        <Table.Cell textAlign="right" collapsing>
                          <Button
                            icon
                            circular
                            primary
                            size="mini"
                            className="voucher-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleStartEdit(i)}
                            disabled={saving || editingIndex != null}
                            title={t("edit") || "Editar"}
                          >
                            <Icon name="pencil" style={{ margin: 0 }} />
                          </Button>
                          <Button
                            icon
                            circular
                            color="red"
                            size="mini"
                            className="voucher-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleDelete(i)}
                            disabled={saving || editingIndex != null}
                            title={t("delete") || "Eliminar"}
                          >
                            <Icon name="trash" style={{ margin: 0 }} />
                          </Button>
                        </Table.Cell>
                      )}
                    </>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <p style={{ color: "#888" }}>
            {currentTotal != null
              ? `${t("current_value") || "Valor actual"}: ${currentTotal} ${currentUnit || ""}`
              : (t("no_records_yet") || "Todavía no hay comprobantes para este mes.")}
          </p>
        )}
        {canEdit && editingIndex == null && (
          <div style={{ display: "flex", gap: "8px", marginTop: "1em", alignItems: "center" }}>
            <Input
              size="small"
              placeholder={t("voucher_number") || "Nº de comprobante"}
              value={voucherNumber}
              onChange={(e) => setVoucherNumber(e.target.value)}
              style={{ flex: 2 }}
            />
            <Input
              size="small"
              type="number"
              placeholder={t("value") || "Valor"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
        )}
        {!canEdit && (
          <Message size="small" style={{ marginTop: "1em" }}>
            {t("no_permission_add_voucher") || "Tu rol no tiene permiso para agregar comprobantes."}
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        {canEdit && editingIndex == null && (
          <Button className="voucher-modal-btn voucher-modal-btn--primary" style={{ minWidth: 110 }} onClick={() => handleSave()} loading={saving} disabled={saving}>
            <Icon name="plus" />
            {t("add") || "Agregar"}
          </Button>
        )}
        <Button className="voucher-modal-btn voucher-modal-btn--cancel" style={{ minWidth: 110 }} onClick={onClose} disabled={saving}>
          {t("close") || "Cerrar"}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

// EditableCell Component
function EditableCell({
  field,
  period,
  item,
  editMode,
  selectedUnits,
  editedValues,
  onChange,
  formatNumberWithThousands,
  convertUnits,
}) {
  const key = `${period}_${field}`;
  const isEdited = editedValues[key] !== undefined;

  // Valor para mostrar (sin conversión para mantener unidades fijas)
  const displayValue = isEdited
    ? editedValues[key].value
    : item?.[field]?.value || "";

  const [localValue, setLocalValue] = useState(displayValue);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Actualizar local value cuando cambie displayValue
  useEffect(() => {
    setLocalValue(displayValue);
    setHasInteracted(false);
  }, [displayValue]);

  const handleFocus = (e) => {
    // Si el valor es 0 y el usuario no ha interactuado aún, limpiar el campo
    if (!hasInteracted && (localValue === 0 || localValue === "0")) {
      setLocalValue("");
    }
    setHasInteracted(true);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);

    // Si el campo está vacío, enviar 0
    const newValue = inputValue === "" ? 0 : parseFloat(inputValue) || 0;
    onChange(field, period, item, newValue);
  };

  // Verificar si el campo tiene unidad configurada
  const hasUnit = selectedUnits[field] && selectedUnits[field] !== "";

  // Verificar si las unidades no coinciden (item tiene unidad diferente a la configurada)
  const unitMismatch =
    item?.[field]?.unit && hasUnit && normalizeUnit(item[field].unit) !== normalizeUnit(selectedUnits[field]);

  if (!editMode) {
    return (
      <Table.Cell textAlign="right">
        {item && item[field] ? (
          <div>
            {formatNumberWithThousands(item[field].value)}
            {unitMismatch && (
              <span
                style={{
                  fontSize: "0.75em",
                  color: "#f57c00",
                  marginLeft: "4px",
                  fontWeight: "bold",
                }}
                title={`Unidad registrada: ${item[field].unit} | Unidad configurada: ${selectedUnits[field]}`}
              >
                ({item[field].unit})
              </span>
            )}
          </div>
        ) : (
          "-"
        )}
      </Table.Cell>
    );
  }

  return (
    <Table.Cell
      textAlign="right"
      style={{
        backgroundColor: isEdited
          ? "#fff9c4"
          : hasUnit
            ? "transparent"
            : "#f5f5f5",
        position: "relative",
        padding: "2px",
      }}
    >
      <input
        type="number"
        step="0.01"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        disabled={!hasUnit}
        style={{
          width: "100%",
          border: isEdited ? "2px solid #fbc02d" : "1px solid #ccc",
          borderRadius: "4px",
          padding: "6px 8px",
          textAlign: "right",
          fontSize: "13px",
          cursor: hasUnit ? "text" : "not-allowed",
          backgroundColor: hasUnit ? "white" : "#f5f5f5",
          color: hasUnit ? "inherit" : "#999",
          MozAppearance: "textfield", // Firefox: quitar flechas
        }}
        placeholder="-"
        title={!hasUnit ? "Debe configurar la unidad primero" : ""}
      />
      {isEdited && (
        <Icon
          name="edit"
          size="small"
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            color: "#fbc02d",
          }}
        />
      )}
      <style>
        {`
          /* Quitar flechas de input number en todos los navegadores */
          input[type=number]::-webkit-outer-spin-button,
          input[type=number]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type=number] {
            -moz-appearance: textfield;
          }
        `}
      </style>
    </Table.Cell>
  );
}
